import { defineStore } from 'pinia';
import { useMenuStore } from './menu';
import { useBrandStore } from './brand';
import { isToday } from '@/utils/Date';
import type { Campaign } from '@/types/campaign';

export const useCampaignStore = defineStore('campaign', () => {
  const publicCampaigns = ref<Campaign[]>([]);
  const customerCampaigns = ref<Campaign[]>([]);
  const isLoading = ref(false);
  const timestamp = ref(new Date(0));

  const fetchCampaigns = async (): Promise<void> => {
    const { $campaignService } = useNuxtApp();
    if (publicCampaigns.value.length || isToday(timestamp.value)) return;

    isLoading.value = true;
    const { data, error } = await $campaignService.getActiveCampaigns();

    if (!data || error) {
      isLoading.value = false;
      return;
    }

    timestamp.value = new Date();
    publicCampaigns.value = data;
    isLoading.value = false;
  };

  const fetchCustomerCampaigns = async (): Promise<void> => {
    const { brandConfig } = useBrandStore();
    const { setServicesMenu } = useMenuStore();
    const { $campaignService } = useNuxtApp();
    const { data, error } = await $campaignService.getActiveCustomerCampaigns();

    if (error || !data) return;

    customerCampaigns.value = data;

    if (brandConfig.additions.servicesMenu) {
      setServicesMenu();
    }
  };

  const hideCustomerCampaigns = () => {
    customerCampaigns.value = [];
    const { setServicesMenu } = useMenuStore();
    const { brandConfig } = useBrandStore();

    if (brandConfig.additions.servicesMenu) {
      setServicesMenu();
    }
  };

  const getCampaigns = (isAssortment: boolean) => {
    const filteredCustomerCampaigns = customerCampaigns.value.filter((c) => c.isAssortment === isAssortment);
    const filteredPublicCampaigns = publicCampaigns.value.filter((c) => c.isAssortment === isAssortment);
    return filteredPublicCampaigns.concat(filteredCustomerCampaigns);
  };

  const campaigns = computed(() => {
    return getCampaigns(false);
  });

  const assortments = computed(() => {
    return getCampaigns(true);
  });

  return {
    baseCampaigns: publicCampaigns,
    customerCampaigns,
    isLoading,
    timestamp,
    fetchCampaigns,
    fetchCustomerCampaigns,
    campaigns,
    assortments,
    hideCustomerCampaigns,
  };
});
